import { KatalComponent } from '../../shared/base';
import { createElement } from '../../shared/utils';

/**
 * @component {kat-label} KatalLabel Label that can be used to give context to a separate element
 * @status Production
 * @example Basic {"text": "Password", "variant": "", "state":"", "emphasis":""}
 * @example Constraint {"text": "Passwords must contain one of each of the following: letter, number, symbol", "variant": "constraint", "state":""}
 * @example Constraint-Error {"text": "Passwords must contain one of each of the following: letter, number, symbol", "emphasis":"", "variant": "constraint", "state":"error"}
 * @example Constraint-With-Emphasis {"text": "Passwords must contain one of each of the following: letter, number, symbol", "variant": "constraint", "state":"", "emphasis": "Attention"}
 * @example Constraint-With-Emphasis-Error {"text": "Passwords must contain one of each of the following: letter, number, symbol", "emphasis":"Attention", "variant": "constraint", "state":"error"}
 * @example WithTooltip {"text": "Password", "tooltip-text": "Passwords must contain one of each of the following: letter, number, symbol"}
 * @example WithCustomTooltip {
 *   "text": "Password",
 *   "tooltip-text": "Passwords must contain one of each of the following: letter, number, symbol",
 *   "tooltip-position": "right",
 *   "tooltip-trigger-icon": "warning"
 * }
 */
export class KatalLabel extends KatalComponent {
  static get observedAttributes() {
    return [
      'emphasis',
      'text',
      'state',
      'for',
      'variant',
      'tooltip-text',
      'tooltip-position',
      'tooltip-trigger-icon',
    ];
  }

  constructor() {
    super(KatalLabel.observedAttributes);
    this._label = document.createElement('label');
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  /**
   * @param {string} emphasis This is text that shows before the main content of the label.  Gives addtional context to the message
   */
  get emphasis() {
    return this.getAttribute('emphasis') || '';
  }

  set emphasis(value) {
    this.removeOrSetAttribute('emphasis', value);
  }

  /**
   * @param {string} text This is the text that will be displayed to the user
   */
  get text() {
    return this.getAttribute('text') || '';
  }

  set text(value) {
    this.removeOrSetAttribute('text', value);
  }

  /**
   * @param {enum} variant This is the type of label displayed to a user
   * @enum {value} default Normal label used for things such as form inputs
   * @enum {value} constraint Used for providing additional context to a form input such as expected format
   */
  get variant() {
    return this.getAttribute('variant') || 'default';
  }

  set variant(value) {
    this.removeOrSetAttribute('variant', value);
  }

  /**
   * @param {enum} state The type of label displayed.
   * @enum {value} default This is a normal label with no state associated with it
   * @enum {value} error Used when the associated content is invalid and the User must take action to correct it.
   */
  get state() {
    return this.getAttribute('state') || 'default';
  }

  set state(value) {
    this.removeOrSetAttribute('state', value);
  }

  /**
   * @param {string} for This is the element to associate the label with
   */
  get for() {
    return this.getAttribute('for') || '';
  }

  set for(value) {
    this.removeOrSetAttribute('for', value);
  }

  /**
   * @classprop {string} tooltip-text This is the text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
   * set.
   */
  get tooltipText() {
    return this.getAttributeOrDefault('tooltip-text', '');
  }

  set tooltipText(value) {
    this.removeOrSetAttribute('tooltip-text', value);
  }

  /**
   * @classprop {string} tooltip-position This is the position of the tooltip. Defaults to "top".
   */
  get tooltipPosition() {
    return this.getAttributeOrDefault('tooltip-position', 'top');
  }

  set tooltipPosition(value) {
    this.removeOrSetAttribute('tooltip-position', value);
  }

  /**
   * @classprop {string} tooltip-trigger-icon This is the icon that triggers the tooltip next to the label. Defaults to "help_outline".
   */
  get tooltipTriggerIcon() {
    return this.getAttributeOrDefault('tooltip-trigger-icon', 'help_outline');
  }

  set tooltipTriggerIcon(value) {
    this.removeOrSetAttribute('tooltip-trigger-icon', value);
  }

  render() {
    const text = this.text;

    const forWhich = this.for;
    const emphasis = this.emphasis;
    const isStateError = this.state.toLowerCase() === 'error';
    const isConstraintVariant = this.variant.toLowerCase() === 'constraint';

    const classList = [];

    if (isStateError) {
      classList.push('error');
    }

    if (isConstraintVariant) {
      classList.push('constraint');
    }

    if (text === '' && emphasis === '') {
      classList.push('hide');
    }

    let labelElement = this.querySelector('label');
    if (!labelElement) {
      labelElement = document.createElement('label');

      const emphasisElement = document.createElement('span');
      emphasisElement.setAttribute('class', 'emphasis');
      labelElement.appendChild(emphasisElement);

      const textElement = document.createElement('span');
      textElement.setAttribute('class', 'text');
      labelElement.appendChild(textElement);

      this.appendChild(labelElement);
    }

    if (classList.length) {
      labelElement.setAttribute('class', classList.join(' '));
    } else {
      labelElement.removeAttribute('class');
    }

    if (forWhich) {
      labelElement.setAttribute('for', forWhich);
    } else {
      labelElement.removeAttribute('for');
    }

    const textElement = labelElement.querySelector('span.text');
    if (!textElement)
      throw new Error('Missing expected child element "span.text"'); // Should never happen

    const emphasisElement = labelElement.querySelector('span.emphasis');
    if (!emphasisElement)
      throw new Error('Missing expected child element "span.emphasis"'); // Should never happen

    if (text && text.length) {
      textElement.innerText = text;
    } else {
      textElement.innerText = '';
    }

    if (this.tooltipText) {
      if (!this.querySelector('kat-tooltip')) {
        const tooltip = createElement('kat-tooltip', {
          label: this.tooltipText,
          position: this.tooltipPosition,
          size: 'tiny',
          'trigger-icon': this.tooltipTriggerIcon,
        });
        textElement.innerHTML = textElement.innerHTML + '&nbsp;';
        textElement.appendChild(tooltip);
      }
    }

    if (emphasis && emphasis.length) {
      emphasisElement.innerText = emphasis;
      emphasisElement.classList.remove('kat-label-hide');
    } else {
      emphasisElement.innerText = '';
      emphasisElement.classList.add('kat-label-hide');
    }
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    this.render();
  }
}

KatalComponent.register('kat-label', KatalLabel);
